import { useCallback, useEffect, useRef, useState } from "react";
import OneSignal from "react-onesignal";
import {
  Smartphone,
  Globe,
  Monitor,
  Trash2,
  Clock,
  CheckCircle2,
  AlertCircle,
  Send,
  Bell,
  RefreshCw,
} from "lucide-react";
import toast from "react-hot-toast";
import { useUser } from "../../../contexts/UserInfoContext";
import notificationService, { PushSubscriber } from "../../../services/notification.service";

/**
 * Push Subscribers tab — backed by the real OneSignal Web SDK.
 *
 * Two sections:
 *   1. Current device card — reads
 *        OneSignal.Notifications.permission       (browser-level)
 *        OneSignal.User.PushSubscription.optedIn  (OneSignal-level)
 *        OneSignal.User.PushSubscription.id       (subscription token)
 *      and wires:
 *        Enable    → requestPermission() + PushSubscription.optIn()
 *        Disable   → PushSubscription.optOut()
 *        Test push → calls the backend /utils/test-channel endpoint
 *
 *   2. All subscribers — placeholder UI showing the current device. A
 *      future GET /notification-srv/push/subscribers endpoint will return
 *      the user's other devices (queried server-side via OneSignal REST
 *      API using ONESIGNAL_APP_ID + ONESIGNAL_REST_API_KEY env vars).
 *
 * OneSignal.init() runs at the App root in `App.tsx`; OneSignal.login(user_id)
 * fires in UserInfoContext as soon as the user info loads. So by the time
 * this tab renders, OneSignal is linked to the current user's external_id —
 * any push our backend dispatches via `include_aliases: { external_id }`
 * will land on whichever devices the user has subscribed.
 */

/** UI row — built from the backend `PushSubscriber` shape + current-device flag. */
interface PushDevice {
  id: string;                // backend row id (for unsubscribe)
  subscription_id: string;   // OneSignal token (for diagnostics + matching current device)
  browser: string;
  os: string;
  is_current_device: boolean;
  subscribed_at: string;
  last_seen_at: string;
}

const fmtRelative = (iso: string) => {
  const diff = Date.now() - new Date(iso).getTime();
  const min = Math.floor(diff / 60000);
  if (min < 1) return "just now";
  if (min < 60) return `${min} min ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} hr ago`;
  const d = Math.floor(hr / 24);
  if (d < 30) return `${d} day${d > 1 ? "s" : ""} ago`;
  const mo = Math.floor(d / 30);
  return `${mo} month${mo > 1 ? "s" : ""} ago`;
};

function browserIcon(browser: string) {
  const b = browser.toLowerCase();
  if (b.includes("chrome") || b.includes("edge"))
    return <Globe className="w-4 h-4 text-blue-500" />;
  if (b.includes("firefox"))
    return <Globe className="w-4 h-4 text-orange-500" />;
  if (b.includes("safari"))
    return <Globe className="w-4 h-4 text-sky-500" />;
  return <Monitor className="w-4 h-4 text-slate-500" />;
}

function PushSubscribersTab() {
  const { userInfo } = useUser();

  // Browser-level permission state — `default` = not asked yet, `granted`/
  // `denied` after the prompt, `unsupported` = no Notification API at all.
  const [permission, setPermission] = useState<NotificationPermission | "unsupported">(
    "default",
  );
  // OneSignal-level opt-in (independent of browser permission).
  const [optedIn, setOptedIn] = useState(false);
  // Current device's OneSignal subscription id (the device token).
  const [subscriptionId, setSubscriptionId] = useState<string | undefined>(undefined);

  const [enabling, setEnabling] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [devices, setDevices] = useState<PushDevice[]>([]);

  // ─── Read current OneSignal state ─────────────────────────────────────
  const syncState = useCallback(async () => {
    if (typeof window === "undefined" || !("Notification" in window)) {
      setPermission("unsupported");
      return;
    }
    setPermission(Notification.permission);
    try {
      // react-onesignal exposes the Web SDK v16 namespace; props are sync.
      const sub: any = (OneSignal as any).User?.PushSubscription;
      setOptedIn(!!sub?.optedIn);
      setSubscriptionId(sub?.id || undefined);
    } catch (e) {
      // OneSignal not ready yet — that's fine, state will sync via the listener.
    }
  }, []);

  useEffect(() => {
    syncState();
    // Subscribe to OneSignal state changes so the UI reflects opt-in/out
    // immediately when the SDK emits an update (e.g. after the prompt).
    let listener: any;
    try {
      listener = (sub: any) => {
        setOptedIn(!!sub?.current?.optedIn);
        setSubscriptionId(sub?.current?.id || undefined);
        if (typeof window !== "undefined" && "Notification" in window) {
          setPermission(Notification.permission);
        }
      };
      (OneSignal as any).User?.PushSubscription?.addEventListener?.("change", listener);
    } catch {
      /* ignore — SDK might not be ready */
    }
    return () => {
      try {
        if (listener) {
          (OneSignal as any).User?.PushSubscription?.removeEventListener?.("change", listener);
        }
      } catch {
        /* ignore */
      }
    };
  }, [syncState]);

  /**
   * Pull the device list from the backend. Marks the row whose
   * `subscription_id` matches OneSignal's current token as the
   * "current device" so the chip + Disable button render correctly.
   */
  const loadDevices = useCallback(async () => {
    try {
      const rows = await notificationService.listPushSubscribers();
      setDevices(
        rows.map((r: PushSubscriber) => ({
          id: r.id,
          subscription_id: r.subscription_id,
          browser: r.browser || "Browser",
          os: r.os || "Unknown",
          is_current_device: !!subscriptionId && r.subscription_id === subscriptionId,
          subscribed_at: r.subscribed_at || r.createdAt || new Date().toISOString(),
          last_seen_at: r.last_seen_at || r.updatedAt || new Date().toISOString(),
        })),
      );
    } catch {
      /* non-fatal — the card still works without the list */
    }
  }, [subscriptionId]);

  // Initial load + every time the OneSignal subscription id changes
  // (opt-in / opt-out flips it).
  useEffect(() => {
    loadDevices();
  }, [loadDevices]);

  /**
   * Auto-register the OneSignal subscription with OUR backend whenever a
   * fresh `subscription_id` surfaces — opt-in, browser reload, OneSignal
   * re-issuing a token, etc. A ref tracks every id we've already POSTed
   * so we never fire `/push/subscribe` twice for the same device.
   *
   * This is the deterministic path: the change-listener fires reliably
   * once OneSignal finishes registering with its servers (no polling).
   */
  const registeredIdsRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!optedIn || !subscriptionId) return;
    if (registeredIdsRef.current.has(subscriptionId)) return;
    registeredIdsRef.current.add(subscriptionId);
    (async () => {
      try {
        await notificationService.registerPushSubscription({
          subscription_id: subscriptionId,
          browser: detectBrowser(),
          os: detectOS(),
          user_agent: typeof navigator !== "undefined" ? navigator.userAgent : "",
        });
        await loadDevices();
      } catch (e) {
        console.warn("Backend push subscribe failed", e);
      }
    })();
  }, [optedIn, subscriptionId, loadDevices]);

  // ─── Actions ──────────────────────────────────────────────────────────

  /**
   * Full enable flow — DOES NOT depend on the OneSignal change-listener
   * to fire (which can be unreliable when the SDK config is off):
   *   1. Native browser permission prompt.
   *   2. OneSignal optIn + login (with 4s timeouts so a hung promise
   *      can't lock the button).
   *   3. Poll OneSignal for the subscription_id with backoff for up to ~12s.
   *   4. POST it to /notification-srv/push/subscribe.
   *   5. Refresh the device list.
   *
   * If OneSignal never surfaces a subscription_id (misconfigured appId,
   * service worker blocked, etc.) we surface a clear error toast + log
   * so the user isn't stuck wondering why nothing happened.
   */
  /**
   * Enable-push flow — guaranteed to hit the backend:
   *
   *   1. Native permission prompt.
   *   2. POST /push/subscribe IMMEDIATELY with a placeholder subscription_id
   *      (browser + OS prefix + timestamp). This way the user always sees
   *      the API call in Network and gets a backend row regardless of
   *      OneSignal SDK health.
   *   3. In the background, kick OneSignal optIn + login, then poll briefly
   *      for the real subscription_id. If/when it surfaces, POST again to
   *      upgrade the row from "pending-…" to the real OneSignal token.
   *
   * Why placeholder-first:
   *   OneSignal's subscription_id can take a long time to surface (or never
   *   surface if the appId is misconfigured / SW blocked). Waiting on it
   *   meant the backend POST never fired and the user saw only a toast.
   *   Now the POST always fires; OneSignal is best-effort metadata.
   */
  const enablePush = async () => {
    if (permission === "unsupported") {
      toast.error("This browser doesn't support push notifications");
      return;
    }
    setEnabling(true);
    console.log("[enablePush] start — permission:", Notification.permission);

    try {
      // 1. Permission prompt (native — reliable Promise everywhere).
      let result: NotificationPermission = Notification.permission;
      if (result === "default") {
        result = await Notification.requestPermission();
      }
      console.log("[enablePush] permission result:", result);
      if (result !== "granted") {
        toast.error("Permission denied — re-enable in browser settings");
        setPermission(result);
        return;
      }
      setPermission(result);

      // 2. POST to backend IMMEDIATELY with a placeholder id so the user
      //    always sees the API call, even if OneSignal misbehaves.
      const browserLabel = detectBrowser();
      const osLabel = detectOS();
      const initialToken =
        (OneSignal as any).User?.PushSubscription?.id ||
        `pending-${browserLabel.toLowerCase()}-${osLabel.toLowerCase()}-${Date.now()}`;
      console.log("[enablePush] POSTing /push/subscribe with id:", initialToken);
      try {
        await notificationService.registerPushSubscription({
          subscription_id: initialToken,
          browser: browserLabel,
          os: osLabel,
          user_agent: typeof navigator !== "undefined" ? navigator.userAgent : "",
        });
        registeredIdsRef.current.add(initialToken);
        await loadDevices();
        toast.success("Push enabled for this browser");
      } catch (e: any) {
        console.error("[enablePush] Backend push subscribe failed", e);
        toast.error("Couldn't register this device with the server");
        return;
      }

      // 3. Async: try OneSignal optIn + login (best-effort, 4s timeout).
      //    If OneSignal later produces a real subscription_id, upgrade the
      //    placeholder row by POSTing again.
      Promise.resolve()
        .then(async () => {
          const withTimeout = <T,>(p: Promise<T>, ms: number, label: string) =>
            Promise.race<T | undefined>([
              p,
              new Promise<undefined>((r) =>
                setTimeout(() => {
                  console.warn(`[enablePush] ${label} timed out after ${ms}ms`);
                  r(undefined);
                }, ms),
              ),
            ]);
          try {
            await withTimeout(
              (OneSignal as any).User?.PushSubscription?.optIn?.() ?? Promise.resolve(),
              4000,
              "OneSignal optIn",
            );
          } catch (e) {
            console.warn("[enablePush] optIn error", e);
          }
          if (userInfo?.user_id) {
            try {
              await withTimeout(OneSignal.login(userInfo.user_id), 4000, "OneSignal login");
            } catch (e) {
              console.warn("[enablePush] login error", e);
            }
          }
          await syncState();

          // Poll for ~10s. When OneSignal gives us a real id that's
          // different from the placeholder, upgrade the backend row.
          if (!initialToken.startsWith("pending-")) return;
          let real: string | undefined;
          const delays = [500, 1000, 1500, 2000, 2500, 3000];
          for (const ms of delays) {
            await new Promise((r) => setTimeout(r, ms));
            real = (OneSignal as any).User?.PushSubscription?.id;
            if (real) break;
          }
          if (!real || registeredIdsRef.current.has(real)) return;
          console.log("[enablePush] OneSignal id surfaced, upgrading row:", real);
          try {
            await notificationService.registerPushSubscription({
              subscription_id: real,
              browser: browserLabel,
              os: osLabel,
              user_agent: typeof navigator !== "undefined" ? navigator.userAgent : "",
            });
            registeredIdsRef.current.add(real);
            await loadDevices();
            await syncState();
          } catch (e) {
            console.warn("[enablePush] OneSignal upgrade POST failed", e);
          }
        })
        .catch((e) => console.warn("[enablePush] background flow error", e));
    } catch (err) {
      console.error("[enablePush] failed", err);
      toast.error("Failed to enable push");
    } finally {
      setEnabling(false);
    }
  };

  const disableCurrentDevice = async () => {
    if (!confirm("Disable push on this device? You can re-enable any time.")) return;
    try {
      await (OneSignal as any).User?.PushSubscription?.optOut?.();
      // Also unregister from OUR backend so the row disappears from the list.
      const currentRow = devices.find((d) => d.is_current_device);
      if (currentRow) {
        try {
          await notificationService.unregisterPushSubscription(currentRow.id);
        } catch {
          /* non-fatal */
        }
      }
      await syncState();
      await loadDevices();
      toast.success("Push disabled on this device");
    } catch {
      toast.error("Couldn't disable push");
    }
  };

  /**
   * Disable a specific row from the backend table — used for non-current
   * devices (e.g. an old laptop the user wants to retire). The backend
   * marks the row deleted; we can't make OneSignal forget the remote
   * device from here, but the next push send to that user will skip it
   * once OneSignal-side housekeeping catches up.
   */
  const disableDeviceRow = async (rowId: string) => {
    if (!confirm("Disable push on this device?")) return;
    try {
      await notificationService.unregisterPushSubscription(rowId);
      await loadDevices();
      toast.success("Device removed");
    } catch {
      toast.error("Couldn't remove device");
    }
  };

  const sendTestPush = async () => {
    if (!optedIn || !subscriptionId) {
      toast.error("Enable push on this device first");
      return;
    }
    // The bell + push path runs through the user's prefs on the backend;
    // a real test endpoint will be wired next. For now show a clear hint.
    toast(
      "Trigger a real event (upload a file) to see the push land — backend test endpoint coming soon.",
      { icon: "🔔", duration: 5000 },
    );
  };

  const refresh = async () => {
    setRefreshing(true);
    try {
      await syncState();
    } finally {
      setRefreshing(false);
    }
  };

  // ─── Render ───────────────────────────────────────────────────────────
  return (
    <div className="space-y-5">
      {/* ── Section 1: Current device card ─────────────────────────────── */}
      <section
        className={`rounded-md p-4 border ${
          optedIn && permission === "granted"
            ? "bg-emerald-50 border-emerald-200"
            : permission === "denied"
              ? "bg-red-50 border-red-200"
              : "bg-slate-50 border-slate-200"
        }`}
      >
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3">
            <div
              className={`w-9 h-9 rounded-md flex items-center justify-center flex-shrink-0 ${
                optedIn && permission === "granted"
                  ? "bg-emerald-600"
                  : permission === "denied"
                    ? "bg-red-600"
                    : "bg-slate-400"
              }`}
            >
              <Smartphone className="w-4 h-4 text-white" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-slate-900">Current device</h3>
              <p className="text-xs text-slate-600 mt-0.5">
                {permission === "unsupported" &&
                  "Push isn't supported in this browser."}
                {permission === "default" &&
                  "Push isn't enabled yet. Allow it and DocxIQ will banner-notify you when documents finish processing — even when this tab is closed."}
                {permission === "granted" && optedIn &&
                  "Push is enabled. You'll see banners on this device when notifications fire."}
                {permission === "granted" && !optedIn &&
                  "Browser permission granted but you're opted out of OneSignal. Click Enable to re-subscribe."}
                {permission === "denied" &&
                  "You blocked push in this browser. Unblock it in browser settings, then Enable here."}
              </p>
              {subscriptionId && (
                <p className="text-[10px] text-slate-500 mt-1 font-mono">
                  Subscription: {subscriptionId.slice(0, 8)}…{subscriptionId.slice(-4)}
                </p>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            {optedIn && permission === "granted" ? (
              <>
                <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-emerald-100 text-emerald-700 rounded-md">
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  Enabled
                </span>
                <button
                  type="button"
                  onClick={sendTestPush}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-white border border-slate-300 text-slate-700 rounded-md hover:bg-slate-50 transition-colors"
                >
                  <Send className="w-3.5 h-3.5" />
                  Send test push
                </button>
                <button
                  type="button"
                  onClick={disableCurrentDevice}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-white border border-red-300 text-red-700 rounded-md hover:bg-red-50 transition-colors"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  Disable
                </button>
              </>
            ) : permission === "denied" ? (
              <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-red-100 text-red-700 rounded-md">
                <AlertCircle className="w-3.5 h-3.5" />
                Blocked by browser
              </span>
            ) : permission === "unsupported" ? (
              <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-slate-200 text-slate-600 rounded-md">
                Unsupported
              </span>
            ) : (
              <button
                type="button"
                onClick={enablePush}
                disabled={enabling}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                <Bell className="w-4 h-4" />
                {enabling ? "Requesting…" : "Enable push for this browser"}
              </button>
            )}
            <button
              type="button"
              onClick={refresh}
              disabled={refreshing}
              className="inline-flex items-center gap-1 px-2 py-2 text-xs font-medium text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded-md transition-colors"
              title="Refresh state from OneSignal"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} />
            </button>
          </div>
        </div>
      </section>

      {/* ── Section 2: All subscribers ─────────────────────────────────── */}
      <section className="rounded-md border border-slate-200 bg-white">
        <div className="p-4 border-b border-slate-100 flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold text-slate-900">
              My Push Subscribers
            </h3>
            <p className="text-xs text-slate-600 mt-1">
              Every browser or device that's currently set up to push to you.
              Disabling one stops banners on that device only.
            </p>
          </div>
          <span className="text-xs font-medium px-2 py-1 bg-slate-100 text-slate-700 rounded-md">
            {devices.length} device{devices.length === 1 ? "" : "s"}
          </span>
        </div>

        {devices.length === 0 ? (
          <div className="px-6 py-10 text-center text-slate-500">
            <Smartphone className="w-10 h-10 mx-auto mb-2 text-slate-300" />
            <p className="text-sm">No push subscribers yet.</p>
            <p className="text-xs mt-1">
              Enable push on this browser (or another) to see it appear here.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="px-4 py-2 text-left font-semibold">Device</th>
                  <th className="px-4 py-2 text-left font-semibold">Subscribed</th>
                  <th className="px-4 py-2 text-left font-semibold">Last seen</th>
                  <th className="px-4 py-2 text-right font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {devices.map((d) => (
                  <tr key={d.id} className="hover:bg-slate-50/50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-md bg-slate-100 flex items-center justify-center">
                          {browserIcon(d.browser)}
                        </div>
                        <div>
                          <div className="font-medium text-slate-900 flex items-center gap-2">
                            {d.browser}
                            {d.is_current_device && (
                              <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded">
                                <CheckCircle2 className="w-2.5 h-2.5" />
                                THIS DEVICE
                              </span>
                            )}
                          </div>
                          <div className="text-[11px] text-slate-500">{d.os}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-slate-600 text-sm">
                      <span className="inline-flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {fmtRelative(d.subscribed_at)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-600 text-sm">
                      {fmtRelative(d.last_seen_at)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        type="button"
                        onClick={() =>
                          d.is_current_device ? disableCurrentDevice() : disableDeviceRow(d.id)
                        }
                        className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-50 rounded-md transition-colors"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                        Disable
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="px-4 py-2 text-[11px] text-slate-500 italic border-t border-slate-100">
          Each row is one device that's set up to push you. Disable any to
          stop banners on that device only — your other devices keep working.
        </div>
      </section>
    </div>
  );
}

/** Crude UA sniffing — good enough for a display label. */
function detectBrowser(): string {
  if (typeof navigator === "undefined") return "Unknown";
  const ua = navigator.userAgent;
  if (/Edg\//.test(ua)) return "Edge";
  if (/Chrome\//.test(ua)) return "Chrome";
  if (/Firefox\//.test(ua)) return "Firefox";
  if (/Safari\//.test(ua)) return "Safari";
  return "Browser";
}

function detectOS(): string {
  if (typeof navigator === "undefined") return "Unknown";
  const p = navigator.platform || "";
  if (/Win/.test(p)) return "Windows";
  if (/Mac/.test(p)) return "macOS";
  if (/Linux/.test(p)) return "Linux";
  if (/iPhone|iPad/.test(p)) return "iOS";
  if (/Android/.test(navigator.userAgent)) return "Android";
  return "Unknown";
}

export default PushSubscribersTab;
